import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const clio = defineConnector({
  identifier: "clio",
  name: "Clio",
  tagLine: "Legal practice management",
  logo,
  protocol: "http",
  tags: ["productivity"],
  defaultUrlPattern: ({ hostname }) => domainToRegex(hostname),
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your Clio instance details",
      items: [
        {
          kind: "markdown",
          content: "Enter the details for your Clio instance.",
        },
        {
          kind: "input",
          label: "Hostname",
          placeholder: "app.clio.com",
          required: true,
          target: { kind: "configuration", field: "hostname" },
          helperText:
            "The hostname for your Clio region (e.g. app.clio.com for US, eu.app.clio.com for EU, ca.app.clio.com for CA, au.app.clio.com for AU) (e.g. https://app.clio.com)",
        },
      ],
    },
    {
      title: "Create a Clio app",
      items: [
        {
          kind: "markdown",
          content:
            "Go to [Clio Developer Applications](https://app.clio.com/nc/#/settings/developer_applications) and create a new application to get your client ID and client secret.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/clio/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your Clio account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Clio account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: (configuration) =>
      `https://${configuration.hostname}/oauth/authorize`,
    tokenEndpoint: (configuration) =>
      `https://${configuration.hostname}/oauth/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: (configuration) =>
      `https://${configuration.hostname}/api/v4/users/who_am_i`,
  },
  publicEnvVars: ({ hostname }) => ({
    CLIO_URL: hostname ? `https://${hostname}` : "",
  }),
});
